import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Switch } from 'components/ui';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Input, Textarea } from 'components/ui/Form';
import {
  PlusIcon,
  PencilIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { useNotes } from 'hooks/useNotes';
import { toast } from 'sonner';
import { canEditLead } from 'permissions/permissionHelpers';
import { formatDisplayDate } from "../../../utils/dateUtils";
import InfiniteScroll from "react-infinite-scroll-component";
import { invalidateTimelineCache } from "hooks/useTimeline";
import { useAuthContext } from 'app/contexts/auth/context';

const NotesTab = ({ isMobile, leadData, openAddModalExternal = false, setOpenAddModalExternal = null, refreshTrigger = 0, onRefreshParent, scrollTargetId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportantOnly, setShowImportantOnly] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Get lead ID from leadData prop
  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;
  const scrollElementId = scrollTargetId || `notes-scroll-${leadId}`;
  const [search, setSearch] = useState("");

  const {
    notes,
    totalRecords,
    isLoading: loading,
    hasMore,
    error,
    loadMore: loadMoreNotes,
    refresh: refetchNotes,
    createNote,
    updateNote,
    deleteNote
  } = useNotes(leadId, {
    isImportant: showImportantOnly || null,
    search: search,
    pageSize: 10
  });

  useEffect(() => {
    if (refreshTrigger) {
      refetchNotes();
    }
  }, [refreshTrigger, refetchNotes]);

  const { user } = useAuthContext();
  
  const isUserAdminOrRoleOwner = () => {
    if (!user) return false;

    const userRole = (user.UserRole || user.RoleName || "").toString().trim().toLowerCase();
    return user.isAdmin || userRole === "admin" || userRole === "owner";
  };

  const isLeadOwner = () => {
    if (!user || !leadData) return false;
    const currentUserId = (user.userId || user.id || "").toString().trim().toLowerCase();
    const currentUserName = (user.FullName || user.name || "").toString().trim().toLowerCase();
    
    if (!currentUserId && !currentUserName) return false;
    
    const owners = leadData.Owners;
    if (Array.isArray(owners)) {
      return owners.some(owner => {
        const ownerId = (owner.OwnerId || owner.ownerId || "").toString().trim().toLowerCase();
        const ownerName = (owner.OwnerName || owner.ownerName || "").toString().trim().toLowerCase();
        return (ownerId && ownerId === currentUserId) || (ownerName && ownerName === currentUserName);
      });
    } else if (typeof owners === "string") {
      const ownerName = owners.trim().toLowerCase();
      return ownerName && ownerName === currentUserName;
    }
    return false;
  };

  const isNoteCreator = (note) => {
    if (!user || !note) return false;
    const currentUserName = (user.FullName || user.name || "").toString().trim().toLowerCase();
    const currentUserId = (user.userId || user.id || "").toString().trim().toLowerCase();
    
    const noteCreatorName = (note.CreatedByName || "").toString().trim().toLowerCase();
    const noteCreatorId = (note.CreatedBy || note.CreatedByUserId || "").toString().trim().toLowerCase();
    
    return (noteCreatorId && noteCreatorId === currentUserId) || 
           (noteCreatorName && currentUserName && noteCreatorName === currentUserName);
  };

  const canEditNote = (note) => {
    if (isUserAdminOrRoleOwner()) {
      return true;
    }
    if (isNoteCreator(note)) {
      return true;
    }
    return false;
  };


  const SOURCE_TYPE_OPTIONS = [
    { value: 'ALL', label: 'All Sources' },
    { value: 'Note', label: 'Manual Notes' },
    { value: 'STAGE_CHANGE', label: 'Stage Changes' },
    { value: 'FOLLOW_UP', label: 'Activities' },
    { value: 'QUERY_ASSIGNMENT', label: 'Query Assignments' },
    { value: 'DOCUMENT_VERIFIED', label: 'Document Verified' },
    { value: 'DOCUMENT_REJECTED', label: 'Document Rejected' }
  ];

  const [selectedSourceType, setSelectedSourceType] = useState(SOURCE_TYPE_OPTIONS[0]);
  const [searchParams] = useSearchParams();
  const targetNoteId = searchParams.get('noteId');

  // Effect to scroll to specific note if noteId is in URL
  useEffect(() => {
    if (targetNoteId && notes.length > 0) {
      // Small timeout to ensure DOM is fully ready
      const timer = setTimeout(() => {
        // Try all possible ID match patterns
        const element = document.getElementById(`note-card-${targetNoteId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Temporary highlight effect
          element.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2', 'dark:ring-offset-dark-800');
          
          const removeHighlight = setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2', 'dark:ring-offset-dark-800');
          }, 4000);
          
          return () => clearTimeout(removeHighlight);
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [targetNoteId, notes]);

  // Sync external modal control
  useEffect(() => {
    if (openAddModalExternal) {
      setShowAddModal(true);
      // Reset the external state after opening
      if (setOpenAddModalExternal) {
        setOpenAddModalExternal(false);
      }
    }
  }, [openAddModalExternal, setOpenAddModalExternal]);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note", {
        description: "Note description cannot be empty",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (editingNote) {
        // Edit existing note
        const result = await updateNote(
          editingNote.Id || editingNote.NoteId,
          newNote.trim(),
          isImportant,
          editingNote.Status || "Active"
        );

        if (result.success) {
          toast.success("Note updated successfully!");
          resetModalState();
          invalidateTimelineCache(leadId);
          if (onRefreshParent) onRefreshParent();
        } else {
          toast.error("Failed to update note", { description: result.message || "An unexpected error occurred." });
        }
      } else {
        // Add new note
        const result = await createNote(newNote, isImportant);

        if (result.success) {
          toast.success("Note added successfully!");
          resetModalState();
          invalidateTimelineCache(leadId);
          if (onRefreshParent) onRefreshParent();
        } else {
          toast.error("Failed to add note", { description: result.message || "An unexpected error occurred." });
        }
      }
    } catch (err) {
      console.error("Error handling note:", err);
      toast.error(editingNote ? "Failed to edit note" : "Failed to add note");
    } finally {
      setIsSubmitting(false);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Handle delete note initiation
  const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    setShowDeleteModal(true);
  };

  // Confirm delete note
  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;
    
    try {
      setIsSubmitting(true);
      const result = await deleteNote(noteToDelete);
      if (result.success) {
        toast.success("Note deleted successfully!");
        setShowDeleteModal(false);
        setNoteToDelete(null);
        if (onRefreshParent) onRefreshParent();
      } else {
        toast.error("Failed to delete note", { description: result.message || result.error || "An unexpected error occurred." });
      }
    } catch (err) {
      console.error("Error deleting note:", err);
      toast.error("Failed to delete note");
    } finally {
      setIsSubmitting(false);
    }
  };


  // Define what makes a note 'Important' according to user requirement
  const isNoteImportant = (note) => {
    return Boolean(note.IsImportant) && (note.SourceType === 'Note' || note.SourceType === 'NOTE');
  };

  // Filtered notes for display
  const displayedNotes = notes.filter(note => {
    // 1. Important Toggle Filter
    if (showImportantOnly && !isNoteImportant(note)) {
      return false;
    }

    // 2. Source Type Filter
    if (selectedSourceType.value !== 'ALL') {
      if (selectedSourceType.value === 'Note') {
        // Manual notes can be 'Note' or 'NOTE'
        if (note.SourceType !== 'Note' && note.SourceType !== 'NOTE') return false;
      } else {
        if (note.SourceType !== selectedSourceType.value) return false;
      }
    }

    return true;
  });

  // Header count for currently visible notes
  const displayedCount = displayedNotes.length;

  // Handle edit note
  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNote(note.NoteText || note.NoteDescription || "");
    setIsImportant(Boolean(note.IsImportant));
    setShowAddModal(true);
  };

  // Reset modal state
  const resetModalState = () => {
    setShowAddModal(false);
    setEditingNote(null);
    setNewNote("");
    setIsImportant(false);
    setIsSubmitting(false);
  };

  // Loading state removed - handled by global loader

  if (error) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-6"}`}>
        <div className="text-center py-8">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Error Loading Notes
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {typeof error === "string" ? error : error?.message || "Failed to load notes"}
          </p>
          <Button onClick={refetchNotes} variant="outlined" color="primary">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Notes
            </h2>
          </div>
          {canEditLead() ? (
            <Button
              onClick={() => setShowAddModal(true)}
              color="primary"
              disabled={leadData?.IsStudent}
              className={`flex items-center space-x-2 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Note</span>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Notes List with Scroll Container */}
      <div className={scrollTargetId ? "" : "flex-1 overflow-hidden"}>
        <div
          id={scrollTargetId ? undefined : scrollElementId}
          className={scrollTargetId ? "px-1" : "h-full overflow-y-auto px-1"}
          style={scrollTargetId ? {} : { height: "calc(100vh - 20rem)", minHeight: "400px" }}
        >
          {notes.length === 0 ? (
            <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
              <div
                className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} mx-auto mb-4 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20`}
              >
                <PencilIcon
                  className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} text-amber-600 dark:text-amber-400`}
                />
              </div>
              <h3
                className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
              >
                {showImportantOnly ? "No Important Notes" : "No Notes Yet"}
              </h3>
              <p
                className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 mb-6 text-gray-500`}
              >
                {showImportantOnly
                  ? "No important notes found for this lead. Try turning off the filter or mark notes as important."
                  : "Start adding notes to track important information about this lead"}
              </p>
              {canEditLead() ? (
                <Button
                  onClick={() => setShowAddModal(true)}
                  disabled={leadData?.IsStudent}
                  variant="soft"
                  color="primary"
                  className={`${isMobile ? "px-4 py-2 text-sm" : ""} ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add First Note
                </Button>
              ) : null}
            </Card>
          ) : (
            <InfiniteScroll
              dataLength={displayedNotes.length}
              next={loadMoreNotes}
              hasMore={hasMore}
              scrollableTarget={scrollElementId}
              loader={
                <div className="flex justify-center py-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
                    <span>Loading more notes...</span>
                  </div>
                </div>
              }
              endMessage={
                notes.length === 0 && (
                  <div className="py-6 text-center text-gray-500">
                    No more notes
                  </div>
                )
              }
            >
              <div className="space-y-4">
                {displayedNotes.map((note, index) => (
                  <Card
                    key={note.LeadNoteId || note.NoteId || `${note.Id || 'note'}-${index}`}
                    id={`note-card-${note.LeadNoteId || note.NoteId || note.Id}`}
                    className="group relative overflow-hidden border border-gray-200 transition-all duration-200 hover:border-gray-300 hover:shadow-lg dark:border-gray-700 dark:hover:border-gray-600"
                  >
                    {/* Important indicator stripe */}
                    {isNoteImportant(note) && (
                      <div className="absolute bottom-0 left-0 top-0 w-1 bg-gradient-to-b from-yellow-400 to-yellow-600"></div>
                    )}
                    <div className="m-1.5">
                      {note.SourceType && (
                        <div className="flex items-center space-x-1">
                          <Badge
                            variant="flat"
                            className={`${
                              note.SourceType === "ACTIVITY"
                                ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
                                : note.SourceType === "STAGE_CHANGE"
                                  ? "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                                  : "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                            } py-0.5 text-xs font-semibold uppercase tracking-wide`}
                          >
                            {note.SourceType.replace("_", " ")}
                          </Badge>
                        </div>
                      )}
                    </div>

                      <div className="p-3">
                      <div className="prose prose-sm max-w-none mb-4">
                        <div className="whitespace-pre-wrap text-base font-normal leading-relaxed text-gray-800 dark:text-gray-200 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-4 [&_ol]:ml-4 [&_p]:my-1">
                          {isNoteImportant(note) && (
                            <StarSolid className="inline-block w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-1.5 -mt-1" />
                          )}
                          <span dangerouslySetInnerHTML={{ __html: note.NoteText || note.NoteDescription || "No content" }} />
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-50 dark:border-dark-700/50 pt-2 mt-auto">
                        <div className="flex items-center gap-4">
                          {note.ModifiedDate && note.ModifiedBy ? (
                            <>
                              <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-dark-400">
                                <ClockIcon className="w-3.5 h-3.5" />
                                <span>
                                  {formatDisplayDate(note.ModifiedDate)} (Edited)
                                </span>
                              </div>

                              <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-dark-400">
                                <span className="text-gray-300 dark:text-dark-600">•</span>
                                <UserIcon className="w-3.5 h-3.5" />
                                <span>
                                  by {note.ModifiedBy}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-dark-400">
                                <ClockIcon className="w-3.5 h-3.5" />
                                <span>
                                  {formatDisplayDate(
                                    note.ActivityDate || note.CreatedDate,
                                  )}
                                </span>
                              </div>

                              {note.CreatedByName && (
                                <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-dark-400">
                                  <span className="text-gray-300 dark:text-dark-600">•</span>
                                  <UserIcon className="w-3.5 h-3.5" />
                                  <span>
                                    by {note.CreatedByName}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {isNoteImportant(note) && (
                            <div className="flex items-center gap-0.5 rounded-full bg-yellow-50 dark:bg-yellow-900/10 px-1.5 py-[1px] text-[9px] font-bold text-yellow-600 dark:text-yellow-500 border border-yellow-100 dark:border-yellow-900/20 shrink-0">
                              <StarSolid className="h-2.5 w-2.5" />
                              <span>Important</span>
                            </div>
                          )}

                          {canEditNote(note) && note.SourceType == "NOTE" ? (
                            <Button
                              onClick={() => handleEditNote(note)}
                              variant="flat"
                              size="sm"
                              className="rounded-lg p-1.5 text-gray-400 opacity-0 transition-opacity duration-200 hover:bg-gray-100 hover:text-primary-600 group-hover:opacity-100 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-primary-400"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                          ) : null}
                          {(isUserAdminOrRoleOwner() || isLeadOwner()) && note.SourceType == "NOTE" ? (
                            <Button
                              onClick={() => handleDeleteClick(note.LeadNoteId || note.NoteId || note.Id)}
                              variant="flat"
                              size="sm"
                              className="rounded-lg p-1.5 text-gray-400 opacity-0 transition-opacity duration-200 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:text-gray-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClose={() => !isSubmitting && setShowDeleteModal(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-sm transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
              <div className="flex flex-col items-center text-center">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                </div>
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Delete Note
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete this note? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <Button
                  onClick={() => setShowDeleteModal(false)}
                  variant="outlined"
                  color="neutral"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteNote}
                  color="error"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    "Delete Note"
                  )}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Add Note Modal */}
      <Transition appear show={showAddModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-md max-h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {editingNote ? "Edit Note" : "Add New Note"}
                  </DialogTitle>
                  <Button
                    onClick={resetModalState}
                    variant="flat"
                    isIcon
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Note
                    </label>
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Enter your note here..."
                      rows={6}
                      className="w-full resize-none min-h-[140px]"
                      autoFocus
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full transition-colors duration-200 ${isImportant ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        {isImportant ? (
                          <StarSolid className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                        ) : (
                          <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="isImportant"
                          onClick={() => setIsImportant(!isImportant)}
                          className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                          Mark as important
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {isImportant ? "This note will be highlighted" : "This note will be regular"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="isImportant"
                      checked={isImportant}
                      onCheckedChange={(event) => {
                        const checked = event.target ? event.target.checked : event;
                        setIsImportant(checked);
                      }}
                      onChange={(event) => {
                        const checked = event.target ? event.target.checked : event;
                        setIsImportant(checked);
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <Button
                    onClick={resetModalState}
                    variant="outlined"
                    color="neutral"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddNote}

                    color="primary"
                    disabled={isSubmitting || !newNote.trim()}
                    className="min-w-[100px]"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        {editingNote ? "Updating..." : "Adding..."}
                      </div>
                    ) : (
                      editingNote ? "Update Note" : "Add Note"
                    )}
                  </Button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </div>
  );
};

export default NotesTab;